import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Loader2, X, ChevronLeft, Users, CalendarDays, ClipboardList,
} from 'lucide-react'
import { DateRangePicker } from '../../kit'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState, useEphemeralState } from '../../lib/persist.js'
import { useMe } from '../../lib/me.js'
import { isAdmin } from '../../lib/role.js'
import { POSITIONS, indexBranches, fullName } from '../../lib/employee.js'
import { nameInitial } from '../../lib/text.js'
import {
  TIMETABLE_TYPES, relabelBranch, fmtDate, fmtMoney, fmtTime,
  clockinTs, clockoutTs, lateMinutes, otValue, travelFee,
  rowStatus, payrollPeriod, sortSpec, normSummary, totalSummary, MAX_RANGE_DAYS,
  shiftTypeTone, dateSpineDesc,
} from '../../lib/clockin.js'
import ClockinEditor from '../../components/employee/ClockinEditor.jsx'
import Pager from '../../components/Pager.jsx'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'
import { EmployeeTrigger } from '../../components/employee/EmployeeDrawer.jsx'

// One group's detail is one person's month (~31 rows) or one day's roster
// (~100) — both fit a single page, so the detail table rarely pages at all.
const DETAIL_PER_PAGE = 200
const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

function Avatar({ name, small = false }) {
  return (
    <span className={`${small ? 'w-7 h-7 text-[12px]' : 'w-8 h-8 text-[13px]'} rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100 flex items-center justify-center font-semibold shrink-0`}>
      {nameInitial(name)}
    </span>
  )
}

function StatusPill({ row }) {
  const s = rowStatus(row)
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 ${s.tone}`}>{s.label}</span>
}

/* ── conclusion table ─────────────────────────────────────────────────────
 * The report's front door: every group in the period on one screen, totalled,
 * with the row you click driving everything below it. Mirrors the branch table
 * on the Treatments page. */
function SummaryTable({ rows, totals, byEmployee, selectedKey, onSelect, loading, err }) {
  const COLS = 8
  const num = (v, tone = 'text-slate-700') => (
    <td className={`px-4 py-2.5 text-right tabular-nums ${v ? tone : 'text-slate-300'}`}>{v ? v.toLocaleString() : '—'}</td>
  )
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
              <th className="px-4 py-2.5">{byEmployee ? 'พนักงาน' : 'วันที่'}</th>
              <th className="px-4 py-2.5 text-right">กะ</th>
              <th className="px-4 py-2.5 text-right">ลงเวลา</th>
              <th className="px-4 py-2.5 text-right">ขาด</th>
              <th className="px-4 py-2.5 text-right">สาย (ครั้ง)</th>
              <th className="px-4 py-2.5 text-right">สาย (นาที)</th>
              <th className="px-4 py-2.5 text-right">OT / ออกก่อน</th>
              <th className="px-4 py-2.5 text-right">เดินทาง</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={COLS} className="px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังสรุป…</td></tr>}
            {!loading && err && <tr><td colSpan={COLS} className="px-4 py-12 text-center text-rose-500">{err}</td></tr>}
            {!loading && !err && !rows.length && <tr><td colSpan={COLS} className="px-4 py-12 text-center text-slate-400">ไม่พบตารางเวรในช่วงวันที่ที่เลือก</td></tr>}
            {!loading && !err && rows.map((r) => {
              const on = r.key === selectedKey
              return (
                <tr key={r.key} onClick={() => onSelect(on ? null : r)}
                  className={`border-b border-slate-50 last:border-0 cursor-pointer transition ${on ? 'bg-brand-50/70' : 'hover:bg-brand-50/40'}`}>
                  <td className="px-4 py-2.5 relative">
                    {on && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-brand-600" />}
                    <div className="flex items-center gap-2.5">
                      {/* Two targets in one row: the ROW picks the group to break
                          down below, the avatar+name opens the person. */}
                      {byEmployee ? (
                        <EmployeeTrigger employeeId={r.employee_id} className="items-center gap-2.5">
                          <Avatar name={r.nickname || r.name || r.code} small />
                          <div className="min-w-0">
                            <div className="font-medium text-slate-800 truncate">{r.code || r.name || `#${r.employee_id}`}</div>
                            {r.name && <div className="text-[12px] text-slate-400 truncate">{r.name}</div>}
                          </div>
                        </EmployeeTrigger>
                      ) : (
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">{fmtDate(r.date)}</div>
                        </div>
                      )}
                      {r.leave > 0 && <span className="ml-1 shrink-0 rounded-md bg-violet-50 text-violet-700 ring-1 ring-violet-200 px-1.5 py-0.5 text-[11px]">ลา {r.leave}</span>}
                      {on && <span className="shrink-0 rounded-md bg-brand-100 text-brand-700 px-1.5 py-0.5 text-[11px] font-medium">กำลังดู</span>}
                    </div>
                  </td>
                  {num(r.shifts, 'text-slate-800 font-medium')}
                  {num(r.punched, 'text-emerald-600')}
                  {num(r.missing, 'text-amber-600')}
                  {num(r.lateCount, 'text-rose-600')}
                  {num(r.lateMins, 'text-rose-600')}
                  <td className={`px-4 py-2.5 text-right tabular-nums ${r.ot < 0 ? 'text-rose-600' : r.ot > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>
                    {r.ot ? fmtMoney(r.ot) : '—'}
                  </td>
                  {num(r.travel)}
                </tr>
              )
            })}
            {!loading && !err && rows.length > 0 && (
              <tr className="bg-slate-50/70 font-semibold text-slate-800">
                <td className="px-4 py-2.5">รวม</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{totals.shifts.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{totals.punched.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-amber-700">{totals.missing.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-rose-700">{totals.lateCount.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-rose-700">{totals.lateMins.toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{totals.ot ? fmtMoney(totals.ot) : '—'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{totals.travel ? fmtMoney(totals.travel) : '—'}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// One shift row in the detail table. The column the group already fixed
// (the person, or the day) is dropped rather than repeated on every line.
function ClockinRow({ row: r, byEmployee, name, initial, code, branch, onClick }) {
  const late = lateMinutes(r)
  const ot = otValue(r)
  const fee = travelFee(r)
  return (
    <tr onClick={onClick} className="border-b border-slate-50 last:border-0 hover:bg-brand-50/40 cursor-pointer transition">
      {byEmployee ? (
        <td className="px-4 py-3 align-top text-slate-600 whitespace-nowrap">{fmtDate(r.date)}</td>
      ) : (
        <td className="px-4 py-3 align-top">
          <EmployeeTrigger employeeId={r.employee_id} className="items-center gap-2.5">
            <Avatar name={initial || name} />
            <div className="min-w-0">
              <div className="font-medium text-slate-800 truncate">{name}</div>
              {code && <div className="text-[12px] text-slate-400 truncate">{code}</div>}
            </div>
          </EmployeeTrigger>
        </td>
      )}
      <td className="px-4 py-3 align-top">
        <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[12px] font-medium ring-1 ${shiftTypeTone(r.type)}`}>
          {r.type || '—'}
        </span>
        <div className="text-[12px] text-slate-400 mt-0.5">{branch}</div>
      </td>
      <td className="px-4 py-3 align-top">
        <div className="tabular-nums text-slate-700">{fmtTime(clockinTs(r))}</div>
        {late > 0 && <div className="text-[12px] font-medium text-rose-600 tabular-nums">สาย {late} น.</div>}
      </td>
      <td className="px-4 py-3 align-top">
        <div className="tabular-nums text-slate-700">{fmtTime(clockoutTs(r))}</div>
        {ot !== 0 && (
          <div className={`text-[12px] font-medium tabular-nums ${ot < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
            {ot < 0 ? 'ออกก่อน' : 'OT'} {fmtMoney(ot)}
          </div>
        )}
        {/* Why OT was (or wasn't) paid — the approval flag drives Xano's own
            clockout_price rule, so it belongs beside the number it explains. */}
        {r.allow_OT === true && (
          <div className="text-[11.5px] text-emerald-600 mt-0.5">อนุมัติ OT</div>
        )}
        {r.special_OT === true && (
          <div className="text-[11.5px] text-indigo-600 mt-0.5">OT พิเศษ</div>
        )}
      </td>
      <td className={`px-4 py-3 align-top text-right tabular-nums ${fee ? 'text-slate-700' : 'text-slate-400'}`}>{fee ? fmtMoney(fee) : '—'}</td>
      <td className="px-4 py-3 align-top"><StatusPill row={r} /></td>
    </tr>
  )
}

function MiniStat({ label, value, tone = 'text-slate-800', dot }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-400">
        {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}{label}
      </div>
      <div className={`text-[17px] font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  )
}

export default function EmployeeClockin() {
  const me = useMe()
  const [branches, setBranches] = useState([])
  const [employees, setEmployees] = useState([])

  const [period, setPeriod] = useEphemeralState('emp:ci:period', () => payrollPeriod())
  // Shared with every พนักงาน tab — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter()
  const [branchId, setBranchId] = usePersistedState('emp:ci:branch', '')
  const [shiftType, setShiftType] = usePersistedState('emp:ci:type', '')
  const [position, setPosition] = usePersistedState('emp:ci:pos', '')
  const [groupBy, setGroupBy] = usePersistedState('emp:ci:group', 'employee')

  const [summary, setSummary] = useState([])
  const [sumLoading, setSumLoading] = useState(true)
  const [sumErr, setSumErr] = useState('')
  const sumReq = useRef(0)

  const [selected, setSelected] = useState(null)   // a normalised summary row
  const [rows, setRows] = useState([])
  const [detTotal, setDetTotal] = useState(0)
  const [detPageTotal, setDetPageTotal] = useState(1)
  const [detPage, setDetPage] = useState(1)
  const [detLoading, setDetLoading] = useState(false)
  const [detErr, setDetErr] = useState('')
  const detReq = useRef(0)

  const [editing, setEditing] = useState(null)
  const [tick, setTick] = useState(0)   // bumped after a save, to refetch both halves

  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    cachedGet('/api/hr/employees', { per_page: 500, active: 'true' })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  const branchName = useMemo(() => indexBranches(branches), [branches])
  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])
  const nameOf = (row) => {
    const e = empById.get(Number(row?.employee_id))
    return e ? fullName(e) : (row?.employee?.code || `#${row?.employee_id ?? '—'}`)
  }
  const codeOf = (row) => row?.employee?.code || empById.get(Number(row?.employee_id))?.code || ''
  // Prefix-free: nameInitial('น.ส.สุนิสา') is 'น', shared by every นาย/นาง/น.ส.
  const initialOf = (row) => {
    const e = empById.get(Number(row?.employee_id))
    return e?.nickname || e?.name || row?.employee?.code || ''
  }

  const byEmployee = groupBy === 'employee'
  const filters = {
    start_date: period.start,
    end_date: period.end,
    employee_id: employeeId || undefined,
    timetable_branch_id: branchId || undefined,
    timetable_type: shiftType || undefined,
    position: position || undefined,
  }
  const filterKey = JSON.stringify(filters) + groupBy

  /* ── conclusion: one grouped query, whole period ──
   * `resetSelection` separates the two reasons to reload: changing a filter
   * invalidates the open group, but saving a punch must LEAVE it open — and
   * re-point it at the refreshed row, or the detail header would keep showing
   * the totals from before the edit. Filtered to ONE person (grouped by พนักงาน),
   * the summary is a single row, so it opens that person's detail instead — the
   * same as การลา. */
  function loadSummary(resetSelection) {
    const id = ++sumReq.current
    setSumLoading(true); setSumErr('')
    if (resetSelection) setSelected(null)
    apiGet('/api/hr/clockin-summary', { ...filters, group_by: groupBy })
      .then((r) => {
        if (id !== sumReq.current) return
        const items = (Array.isArray(r) ? r : (r?.items || [])).map(normSummary)
        setSummary(items)
        if (resetSelection && byEmployee && employeeId) {
          setSelected(items.find((x) => Number(x.employee_id) === Number(employeeId)) || null)
        } else if (!resetSelection) setSelected((s) => (s ? items.find((x) => x.key === s.key) || null : null))
      })
      .catch((e) => { if (id === sumReq.current) setSumErr(String(e.message || e)) })
      .finally(() => { if (id === sumReq.current) setSumLoading(false) })
  }
  useEffect(() => { loadSummary(true) }, [filterKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => totalSummary(summary), [summary])

  /* ── detail: the rows behind the clicked group ── */
  useEffect(() => { setDetPage(1) }, [selected?.key])

  useEffect(() => {
    if (!selected) { setRows([]); setDetErr(''); return }
    const id = ++detReq.current
    setDetLoading(true); setDetErr('')
    const scoped = byEmployee
      ? { ...filters, employee_id: String(selected.employee_id) }
      : { ...filters, start_date: selected.date, end_date: selected.date }
    apiGet('/api/hr/clockin', {
      ...scoped, sort: sortSpec(groupBy), page: detPage, per_page: DETAIL_PER_PAGE,
    })
      .then((r) => {
        if (id !== detReq.current) return
        const items = Array.isArray(r) ? r : (r?.items || [])
        setRows(items)
        setDetTotal(r?.itemsTotal ?? items.length)
        setDetPageTotal(r?.pageTotal ?? 1)
      })
      .catch((e) => { if (id === detReq.current) setDetErr(String(e.message || e)) })
      .finally(() => { if (id === detReq.current) setDetLoading(false) })
  }, [selected?.key, detPage, filterKey, tick]) // eslint-disable-line react-hooks/exhaustive-deps

  // After a save both halves are stale: the group's totals and the row itself.
  function reloadAll() {
    setEditing(null)
    setTick((t) => t + 1)   // refetch the detail rows
    loadSummary(false)      // refetch the totals, keeping the group open
  }

  /* Every day of the period, newest first — a day with NO employee_timetable
   * row is a finding ("never rostered"), and a table that only renders what the
   * query returned makes it invisible. Only for the employee axis (a date group
   * IS one day), and only when the detail fits one page, since a spine over a
   * paged subset would pair real rows with the wrong days. */
  const detailItems = useMemo(() => {
    if (!byEmployee || detPageTotal > 1) return rows.map((r) => ({ kind: 'row', row: r }))
    const byDate = new Map()
    for (const r of rows) {
      const k = String(r.date || '').slice(0, 10)
      if (!byDate.has(k)) byDate.set(k, [])
      byDate.get(k).push(r)
    }
    const out = []
    for (const d of dateSpineDesc(period.start, period.end)) {
      const list = byDate.get(d)
      if (list?.length) list.forEach((r) => out.push({ kind: 'row', row: r }))
      else out.push({ kind: 'empty', date: d })
    }
    return out
  }, [rows, byEmployee, detPageTotal, period.start, period.end])

  const noRosterDays = useMemo(
    () => detailItems.filter((i) => i.kind === 'empty').length,
    [detailItems],
  )

  const hasFilter = employeeId || branchId || shiftType || position
  const myEmployeeId = Number(me?.employee_id) || 0
  const admin = isAdmin(me)
  const title = selected && (byEmployee
    ? (selected.code || selected.name || `#${selected.employee_id}`)
    : fmtDate(selected.date))

  return (
    <div className="max-w-[1280px]">
      <p className="text-[13.5px] text-slate-500 mb-5">
        สรุปการลงเวลาตามรอบเงินเดือน — คลิกแถวในตารางสรุปเพื่อดูรายละเอียดรายกะ
      </p>

      {/* period — the kit picker. allowFuture: a round runs to the 25th and the
          roster is planned ahead; maxDays keeps the 31-day cap the proxy expects. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <DateRangePicker value={period} onChange={setPeriod} allowFuture maxDays={MAX_RANGE_DAYS}
          presets={['thisRound', 'lastRound', 'today', 'last7']} className="w-[340px]" />
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <EmployeePicker value={employeeId} onChange={setEmployeeId} allowAll className="min-w-[240px]" />
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT}>
          <option value="">ทุกสาขา</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
        </select>
        <select value={shiftType} onChange={(e) => setShiftType(e.target.value)} className={SELECT}>
          <option value="">ทุกประเภทกะ</option>
          {TIMETABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={position} onChange={(e) => setPosition(e.target.value)} className={SELECT}>
          <option value="">ทุกตำแหน่ง</option>
          {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {hasFilter && (
          <button onClick={() => { setEmployeeId(''); setBranchId(''); setShiftType(''); setPosition('') }}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> ล้าง
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[12.5px] text-slate-400">จัดกลุ่ม</span>
          <div className="inline-flex rounded-xl ring-1 ring-slate-200 bg-white p-0.5">
            {[
              { key: 'employee', label: 'พนักงาน', icon: Users },
              { key: 'date', label: 'วันที่', icon: CalendarDays },
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setGroupBy(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-[13px] font-medium transition ${
                  groupBy === key ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* The summary and the detail are two VIEWS of the same report, not two
          stacked tables: once a group is picked the conclusion collapses so the
          detail owns the screen, and the back control restores it. */}
      {!selected && (
        <>
          <SummaryTable
            rows={summary} totals={totals} byEmployee={byEmployee}
            selectedKey={null} onSelect={setSelected}
            loading={sumLoading} err={sumErr}
          />
          {!sumLoading && !sumErr && summary.length > 0 && (
            <p className="text-[12.5px] text-slate-400 mt-3">
              คลิกแถว{byEmployee ? 'พนักงาน' : 'วันที่'}เพื่อดูรายละเอียดการลงเวลาแต่ละกะ
            </p>
          )}
        </>
      )}

      {/* ── detail: the rows behind the selected group ── */}
      {selected && (
        <>
          <div className="flex items-center justify-between gap-4 mb-3 text-[12.5px]">
            {/* Opened by the filter, "back" means everyone (as on การลา). */}
            <button onClick={() => (byEmployee && employeeId ? setEmployeeId('') : setSelected(null))}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 -ml-1 rounded-lg font-medium text-slate-600 hover:bg-slate-100">
              <ChevronLeft size={15} /> {byEmployee && employeeId ? 'ดูพนักงานทุกคน' : 'กลับไปตารางสรุป'}
            </button>
            <div className="flex items-center gap-1.5 text-slate-400 min-w-0">
              <ClipboardList size={14} className="shrink-0" />
              <span className="truncate">กำลังดู{byEmployee ? 'พนักงาน' : 'วันที่'}</span>
              <span className="font-semibold text-brand-700 truncate">{title}</span>
              <span className="shrink-0">· {selected.shifts} กะ · ลงเวลา {selected.punched}</span>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                {byEmployee
                  ? <Avatar name={selected.nickname || selected.name || selected.code} />
                  : <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100 flex items-center justify-center shrink-0"><CalendarDays size={15} /></span>}
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-slate-900 truncate">
                    รายละเอียด{byEmployee ? 'พนักงาน' : 'วันที่'} · {title}
                  </div>
                  <div className="text-[12.5px] text-slate-500">
                    {byEmployee
                      ? `${selected.name || ''} · ${selected.shifts} กะ · ${selected.days} วัน${noRosterDays ? ` · ไม่มีตารางเวร ${noRosterDays} วัน` : ''}`
                      : `${selected.people} คน · ${selected.shifts} กะ`}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 mt-4 rounded-xl bg-slate-50 px-4 py-3">
                <MiniStat label="ลงเวลาแล้ว" value={selected.punched.toLocaleString()} tone="text-emerald-600" dot="bg-emerald-500" />
                <MiniStat label="ขาดลงเวลา" value={selected.missing.toLocaleString()} tone={selected.missing ? 'text-amber-600' : 'text-slate-800'} dot="bg-amber-500" />
                <MiniStat label="สาย (ครั้ง)" value={selected.lateCount.toLocaleString()} tone={selected.lateCount ? 'text-rose-600' : 'text-slate-800'} dot="bg-rose-500" />
                <MiniStat label="สาย (นาที)" value={selected.lateMins.toLocaleString()} tone={selected.lateMins ? 'text-rose-600' : 'text-slate-800'} />
                <MiniStat label="OT / ออกก่อน" value={selected.ot ? fmtMoney(selected.ot) : '—'} tone={selected.ot < 0 ? 'text-rose-600' : selected.ot > 0 ? 'text-emerald-600' : 'text-slate-800'} />
                <MiniStat label="ค่าเดินทาง" value={selected.travel ? fmtMoney(selected.travel) : '—'} />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-[13.5px]">
                <thead>
                  <tr className="text-left text-[12px] font-medium text-slate-400 border-b border-slate-100">
                    <th className="px-4 py-2.5">{byEmployee ? 'วันที่' : 'พนักงาน'}</th>
                    <th className="px-4 py-2.5">กะ / สาขา</th>
                    <th className="px-4 py-2.5">เข้า · สาย</th>
                    <th className="px-4 py-2.5">ออก · OT / ออกก่อน</th>
                    <th className="px-4 py-2.5 text-right">เดินทาง</th>
                    <th className="px-4 py-2.5">สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {detLoading && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400"><Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…</td></tr>}
                  {!detLoading && detErr && <tr><td colSpan={6} className="px-4 py-12 text-center text-rose-500">{detErr}</td></tr>}
                  {!detLoading && !detErr && !detailItems.length && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">ไม่พบรายการ</td></tr>}
                  {!detLoading && !detErr && detailItems.map((it) => (
                    it.kind === 'empty' ? (
                      // Muted, but a "this person was never rostered" day is a
                      // finding to notice — too faint and it reads as a spacer.
                      <tr key={`e${it.date}`} className="border-b border-slate-100 last:border-0 bg-slate-100/80">
                        <td className="px-4 py-2.5 align-top text-slate-500 whitespace-nowrap">{fmtDate(it.date)}</td>
                        <td colSpan={5} className="px-4 py-2.5 align-top text-[12.5px] text-slate-500">ไม่มีตารางเวร</td>
                      </tr>
                    ) : (
                      <ClockinRow
                        key={it.row.id} row={it.row} byEmployee={byEmployee}
                        name={nameOf(it.row)} initial={initialOf(it.row)} code={codeOf(it.row)}
                        branch={relabelBranch(branchName.get(Number(it.row.branch_id)) || '')}
                        onClick={() => setEditing(it.row)}
                      />
                    )
                  ))}
                </tbody>
              </table>
            </div>

            {detPageTotal > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 text-[13px] text-slate-500">
                <span>{detTotal.toLocaleString()} รายการ</span>
                <Pager page={detPage} pageTotal={detPageTotal} onChange={setDetPage} disabled={detLoading} size="sm" />
              </div>
            )}
          </div>
        </>
      )}

      {editing && (
        <ClockinEditor
          row={editing}
          employeeName={nameOf(editing)}
          branchName={branchName.get(Number(editing.branch_id)) || ''}
          selfEdit={!admin && myEmployeeId > 0 && Number(editing.employee_id) === myEmployeeId}
          onClose={() => setEditing(null)}
          onSaved={reloadAll}
        />
      )}
    </div>
  )
}
